import chroma from "chroma-js";
import { t } from "i18next";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { timeStringToMilliseconds } from "./durations";
import FastAPI from "../api/FastAPI";
import COLORS from "../constants/colors";
import { API_DATE_FORMAT, TIME_FORMAT_LONG } from "../constants/dateFormat";
import {
  DEFAULT_MEDIA_COLOR,
  DROPZONE_BORDER_SIZE,
  MEDIA_LIBRARY_ITEM,
  MIN_SIZE_SHOW_LABEL_ITEM_HORIZONTAL,
  MIN_SIZE_SHOW_LABEL_ITEM_VERTICAL,
  OBJECT_TYPES_SCHEDULE_DROPZONE,
  OBJECT_TYPES_SCHEDULE_TEMPLATE,
  OFFSET_HOURS,
} from "../constants/fast";
import { AD_BREAK, MEDIA_FORMATS } from "../constants/media";
import { ComputedTimelineItemsType, TimeboundKeys } from "../types";

import type { QueryClient } from "@tanstack/react-query";
import type { Moment, unitOfTime } from "moment";
import type { Truncated } from "okast-timeline";
import type Timeslot from "okast-timeslot";
import type { VariantTimeslot } from "okast-timeslot";
import type { CSSProperties } from "react";

import type { Query } from "../api/BaseAPI";
import type {
  GetScheduleAllScheduleListResponse,
  Template,
} from "../api/FastAPI.types";
import type { TemplateOption } from "../components/fast_channels/calendar/CalendarSchedulingModal";
import type {
  SchedulingDropzoneItem,
  SchedulingTimeslotItem,
  TemplateDropzoneItem,
} from "../components/fast_channels/timeline/timeline.types";
import type { DistributionFormat } from "../constants/fast";
import type { MediaFormat, MediaFormatWithAdBreak } from "../constants/media";
import type {
  AccSchedulingTimeslotItems,
  KeyWithTranslation,
  Media,
} from "../types";

interface GetCurrentTimelineQueryReturn {
  nextQuery: Query<GetScheduleAllScheduleListResponse>["queryKey"];
  previousQuery: Query<GetScheduleAllScheduleListResponse>["queryKey"];
  currentQuery: Query<GetScheduleAllScheduleListResponse>["queryKey"];
}

export function getCurrentTimelineQuery({
  currentDay,
  channelId,
}: {
  currentDay: string;
  channelId: string;
}): GetCurrentTimelineQueryReturn {
  const calculatePeriod = (
    startOffsetDays: number,
    endOffsetDays: number,
  ): { periodStart: string; periodEnd: string } => ({
    periodStart: moment(currentDay)
      .add(startOffsetDays, "days")
      .startOf("day")
      .subtract(OFFSET_HOURS, "hours")
      .format(),
    periodEnd: moment(currentDay)
      .add(endOffsetDays, "days")
      .startOf("day")
      .add(OFFSET_HOURS, "hours")
      .format(),
  });

  const { periodStart: currentStart, periodEnd: currentEnd } = calculatePeriod(
    0,
    1,
  );
  const currentGetScheduleAllScheduleListQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleAllScheduleList,
    params: [{ channelId, periodStart: currentStart, periodEnd: currentEnd }],
  });

  const { periodStart: nextStart, periodEnd: nextEnd } = calculatePeriod(1, 2);
  const nextGetScheduleAllScheduleListQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleAllScheduleList,
    params: [{ channelId, periodStart: nextStart, periodEnd: nextEnd }],
  });

  const { periodStart: prevStart, periodEnd: prevEnd } = calculatePeriod(-1, 0);
  const previousGetScheduleAllScheduleListQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleAllScheduleList,
    params: [{ channelId, periodStart: prevStart, periodEnd: prevEnd }],
  });

  return {
    currentQuery: currentGetScheduleAllScheduleListQuery.queryKey,
    nextQuery: nextGetScheduleAllScheduleListQuery.queryKey,
    previousQuery: previousGetScheduleAllScheduleListQuery.queryKey,
  };
}

export function prettifyDistributionFormat(
  distributionFormat?: DistributionFormat,
): string {
  if (!distributionFormat) return "";

  const words = distributionFormat.split("_");

  return words
    .map((word, index) => {
      if (index === 1 && index !== words.length - 1) return `${word} +`;
      return word;
    })
    .join(" ");
}

export function invalidateTimelineRelatedQueries({
  queryClient,
  channelId,
  scheduleId,
  currentDay,
}: {
  queryClient: QueryClient;
  channelId: string;
  scheduleId: string;
  currentDay: string;
}): void {
  const getScheduleKpisQueryKey = FastAPI.generateQuery({
    fn: FastAPI.getScheduleKpis,
    params: [
      {
        channelId,
        scheduleId,
        day: moment(currentDay).format(API_DATE_FORMAT),
      },
    ],
  }).queryKey;

  const getCalendarFillingQueryKey = FastAPI.generateQuery({
    fn: FastAPI.getCalendarFilling,
    params: [
      {
        channelId,
        scheduleId,
        periodStart: moment(currentDay)
          .startOf("month")
          .subtract(7, "day")
          .format(API_DATE_FORMAT),
        periodEnd: moment(currentDay)
          .endOf("month")
          .add(7, "day")
          .format(API_DATE_FORMAT),
      },
    ],
  }).queryKey;

  const getScheduleEPGQueryKey = FastAPI.generateQuery({
    fn: FastAPI.getScheduleEPG,
    params: [
      {
        channelId,
        scheduleId,
      },
    ],
  }).queryKey;

  queryClient.invalidateQueries({
    queryKey: getScheduleKpisQueryKey,
  });

  const getCalendarFillingWeekQueryKey = FastAPI.generateQuery({
    fn: FastAPI.getCalendarFillingWeek,
    params: [
      {
        channelId,
        week: moment(currentDay).startOf("week").format(API_DATE_FORMAT),
      },
    ],
  }).queryKey;

  queryClient.invalidateQueries({
    queryKey: getCalendarFillingQueryKey,
  });

  queryClient.invalidateQueries({
    queryKey: getScheduleEPGQueryKey,
  });

  queryClient.invalidateQueries({
    queryKey: getCalendarFillingWeekQueryKey,
  });
}

export function getMediaColorByFormat(
  mediaFormat: MediaFormatWithAdBreak | null | undefined,
  mediasColor: Record<MediaFormat, string> | undefined,
  defaultColor: string,
): string {
  if (!mediaFormat || !mediasColor) {
    return defaultColor;
  }

  if (mediaFormat === AD_BREAK) {
    return mediasColor[MEDIA_FORMATS.ADPOD];
  }

  return mediasColor[mediaFormat] || defaultColor;
}

export const getMediaFormatsWithAdBreak = (
  mediaFormatList: KeyWithTranslation<MediaFormat>[],
): (KeyWithTranslation<MediaFormat> | { key: typeof AD_BREAK })[] => [
  ...mediaFormatList,
  { key: AD_BREAK },
];

export const getTemplateDropZoneItems = (
  timeslot: Timeslot,
): TemplateDropzoneItem[] =>
  timeslot.availableTimeSlots.map((slot) => ({
    start_time: moment(slot.start),
    end_time: moment(slot.end),
    type: OBJECT_TYPES_SCHEDULE_DROPZONE,
    id: `${timeslot.id}-${slot.start}-${slot.end}`,
    group: timeslot.id,
    canMove: false,
    duration: slot.end - slot.start,
    mediaId: "",
  }));

export const getSchedulingDropZoneItems = (
  timeslot: Timeslot,
  variantsTimeSlots?: VariantTimeslot[],
): SchedulingDropzoneItem[] => {
  const dropZoneDefaultSchedule: SchedulingDropzoneItem[] =
    timeslot.availableTimeSlots.map((slot) => ({
      start_time: moment(slot.start),
      end_time: moment(slot.end),
      type: OBJECT_TYPES_SCHEDULE_DROPZONE,
      id: `${timeslot.id}-${slot.start}-${slot.end}`,
      group: timeslot.id,
      canMove: false,
      duration: slot.end - slot.start,
      isFirstVariantMedia: false,
      variantMediaList: [],
      format: null,
      canSelect: false,
      scheduleId: timeslot.id,
      isOnBaseline: true,
      isLoading: false,
      poster: null,
    }));

  const dropZoneVariantSchedule: SchedulingDropzoneItem[] =
    variantsTimeSlots?.reduce(
      (acc: SchedulingDropzoneItem[], variantTimeSlot) => {
        const variantDropZone: SchedulingDropzoneItem[] =
          variantTimeSlot.availableTimeSlots.map((slot) => ({
            start_time: moment(slot.start),
            end_time: moment(slot.end),
            type: OBJECT_TYPES_SCHEDULE_DROPZONE,
            id: `${variantTimeSlot.id}-${slot.start}-${slot.end}`,
            group: variantTimeSlot.id,
            canMove: false,
            duration: slot.end - slot.start,
            isFirstVariantMedia: false,
            variantMediaList: [],
            format: null,
            canSelect: false,
            scheduleId: variantTimeSlot.id,
            isOnBaseline: false,
            isLoading: false,
            poster: null,
          }));

        acc.push(...variantDropZone);

        return acc;
      },
      [],
    ) ?? [];

  return [...dropZoneDefaultSchedule, ...dropZoneVariantSchedule];
};

export const getTimelineItemBackground = ({
  color,
  isDragging,
  isSelected,
  showDropZone,
  canDrop,
  isOver,
}: {
  color: string;
  isDragging?: boolean;
  isSelected?: boolean;
  showDropZone?: boolean;
  canDrop?: boolean;
  isOver?: boolean;
}): {
  backgroundColor: string;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
} => {
  const chromaColor = chroma(color);

  if (showDropZone) {
    if (!canDrop) {
      return {
        backgroundColor:
          "color-mix(in oklab, var(--destructive) 25%, transparent)",
      };
    }

    if (isOver) {
      return {
        backgroundColor: "color-mix(in oklab, var(--primary) 20%, transparent)",
      };
    }

    return {
      backgroundColor: "var(--primary-muted)",
    };
  }

  if (isDragging) {
    return {
      backgroundColor: chromaColor.brighten(0.75).hex(),
    };
  }

  if (isSelected) return { backgroundColor: chromaColor.brighten(0.5).hex() };

  return { backgroundColor: chromaColor.hex() };
};

export const getBorderWidthPosition = (
  borderWidth: string,
  truncated: Truncated,
  isVertical?: boolean,
): {
  borderTop: string | number;
  borderRight: string | number;
  borderBottom: string | number;
  borderLeft: string | number;
} => {
  if (truncated === "start") {
    if (isVertical) {
      return {
        borderTop: 0,
        borderRight: borderWidth,
        borderBottom: borderWidth,
        borderLeft: borderWidth,
      };
    }
    return {
      borderTop: borderWidth,
      borderRight: borderWidth,
      borderBottom: borderWidth,
      borderLeft: 0,
    };
  }

  if (truncated === "end") {
    if (isVertical) {
      return {
        borderTop: borderWidth,
        borderRight: borderWidth,
        borderBottom: 0,
        borderLeft: borderWidth,
      };
    }
    return {
      borderTop: borderWidth,
      borderRight: 0,
      borderBottom: borderWidth,
      borderLeft: borderWidth,
    };
  }

  if (truncated === "start-end") {
    if (isVertical) {
      return {
        borderTop: 0,
        borderRight: borderWidth,
        borderBottom: 0,
        borderLeft: borderWidth,
      };
    }
    return {
      borderTop: borderWidth,
      borderRight: 0,
      borderBottom: borderWidth,
      borderLeft: 0,
    };
  }

  return {
    borderTop: borderWidth,
    borderRight: borderWidth,
    borderBottom: borderWidth,
    borderLeft: borderWidth,
  };
};

export const getTimelineItemBorder = ({
  color,
  isDragging,
  showDropZone,
  canDrop,
  truncated,
  isVertical,
}: {
  color: string;
  isDragging?: boolean;
  showDropZone?: boolean;
  canDrop?: boolean;
  truncated: Truncated;
  isVertical?: boolean;
}): {
  borderTop: string | number;
  borderRight: string | number;
  borderBottom: string | number;
  borderLeft: string | number;
} => {
  if (showDropZone) {
    if (!canDrop) {
      return getBorderWidthPosition(
        `2px dashed ${COLORS.red[400]}`,
        truncated,
        isVertical,
      );
    }

    return getBorderWidthPosition(
      `2px dashed ${COLORS.primary.DEFAULT}`,
      truncated,
      isVertical,
    );
  }

  if (isDragging) {
    return getBorderWidthPosition(`1px solid ${color}`, truncated, isVertical);
  }

  return getBorderWidthPosition(
    `1px solid ${chroma(color).darken().hex()}`,
    truncated,
    isVertical,
  );
};

export const getDropzoneTimelineAcceptedFormats = (
  mediaFormatList: KeyWithTranslation<MediaFormat>[] | undefined,
): string[] => {
  const formats = mediaFormatList
    ? getMediaFormatsWithAdBreak(mediaFormatList)
    : [];

  return [
    ...formats.map((format) => `${MEDIA_LIBRARY_ITEM}-${format.key}`),
    `${MEDIA_LIBRARY_ITEM}-unknown`,
  ];
};

export const getTimelineVisibleDate = (
  currentDay: Moment,
): {
  start: number;
  end: number;
} => {
  const startOfDay = currentDay.clone().startOf("day");
  const endOfDay = currentDay.clone().endOf("day");

  const start = currentDay.clone().add(-1, "hour");
  const end = currentDay.clone().add(11, "hour");

  if (start.isBefore(startOfDay)) {
    return {
      start: startOfDay.valueOf(),
      end: moment(startOfDay).clone().add(12, "hour").valueOf(),
    };
  }

  if (end.isAfter(endOfDay)) {
    return {
      start: moment(endOfDay).clone().add(-12, "hour").valueOf(),
      end: endOfDay.valueOf(),
    };
  }

  return {
    start: start.valueOf(),
    end: end.valueOf(),
  };
};

export enum WeekDays {
  SUNDAY = "sunday",
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
}

export const getWeekDays = (
  firstDayOfWeek: WeekDays = WeekDays.SUNDAY,
): WeekDays[] => {
  const weekDays = Object.values(WeekDays);
  const indexOfFirstDayOfWeek = weekDays.indexOf(firstDayOfWeek);

  return [
    ...weekDays.slice(indexOfFirstDayOfWeek),
    ...weekDays.slice(0, indexOfFirstDayOfWeek),
  ];
};

export const getLastTemplateUpdatedByDay = (
  day: WeekDays,
  templates?: Template[],
): Template | undefined =>
  templates
    ?.filter((template) => template[day])
    .sort((a, b) => a.updated_at.localeCompare(b.updated_at))
    .pop();

export const formatTemplateOption = (template: Template): TemplateOption => {
  const templateLabelSuffix = (): string => {
    if (template.slots_errors_count > 0) {
      return ` (${t("error", { ns: "editor-fast-channel" })})`;
    }
    if (template.slots_count === 0) {
      return ` (${t("empty", { ns: "editor-fast-channel" })})`;
    }

    return "";
  };

  return {
    value: template.uuid,
    label: `${template.name}${templateLabelSuffix()}`,
    isError: template.slots_errors_count > 0,
    isEmpty: template.slots_count === 0,
    monday: template.monday,
    tuesday: template.tuesday,
    wednesday: template.wednesday,
    thursday: template.thursday,
    friday: template.friday,
    saturday: template.saturday,
    sunday: template.sunday,
  };
};

export const computeStartTimeSlotItem = (
  time: string,
  day: Moment,
): Moment | null => {
  const dailyStartTime = day.format(TIME_FORMAT_LONG);
  const dailyStartTimeInMilliseconds = timeStringToMilliseconds(dailyStartTime);
  const timeInMilliseconds = timeStringToMilliseconds(time);

  if (!timeInMilliseconds || !dailyStartTimeInMilliseconds) {
    return null;
  }

  const isNextDay =
    timeInMilliseconds.value < dailyStartTimeInMilliseconds.value;

  const {
    number: { hours, minutes, seconds, milliseconds },
  } = timeInMilliseconds;

  const itemStartTime = day
    .clone()
    .set("hour", hours)
    .set("minute", minutes)
    .set("second", seconds)
    .set("millisecond", milliseconds);

  if (isNextDay) {
    return itemStartTime.clone().add(1, "day");
  }

  return itemStartTime;
};

export const showItemTitle = ({
  isVertical,
  size,
}: {
  isVertical: boolean;
  size: number;
}): boolean =>
  isVertical
    ? size > MIN_SIZE_SHOW_LABEL_ITEM_VERTICAL
    : size > MIN_SIZE_SHOW_LABEL_ITEM_HORIZONTAL;

export const getPreviewMediaItemDimensions = (
  size: number,
  startPosition: number,
  isVertical: boolean,
  truncatedDropzone: Truncated,
  isOnBaseline?: boolean,
): {
  width: string | number;
  height: number | string;
  left: number;
  top: number;
} => {
  const computeSizeByTruncated = (): number => {
    if (truncatedDropzone === "start-end") {
      return size;
    }
    if (truncatedDropzone === "start" || truncatedDropzone === "end") {
      return size - DROPZONE_BORDER_SIZE;
    }

    return size - DROPZONE_BORDER_SIZE * 2;
  };

  const sizeByTruncated = computeSizeByTruncated();

  const computedSize = sizeByTruncated < 0 ? size : sizeByTruncated;

  if (isVertical) {
    return {
      width: "100%",
      height: computedSize,
      left: 0,
      top: startPosition,
    };
  }
  return {
    width: computedSize,
    height: "100%",
    left: isOnBaseline ? startPosition : 0,
    top: 0,
  };
};

export const getBorderRadiusTimelineItem = ({
  truncated,
  isVerticalTimeline,
  isFirstVariantMedia,
  hasVariantMedia,
}: {
  truncated: Truncated;
  isVerticalTimeline?: boolean;
  isFirstVariantMedia?: boolean | null;
  hasVariantMedia?: boolean | null;
}): string => {
  if (truncated === "start-end") {
    return "0px 0px 0px 0px";
  }

  if (truncated === "start") {
    return isVerticalTimeline ? "0px 0px 6px 6px" : "0px 6px 6px 0px";
  }

  if (truncated === "end") {
    if (isVerticalTimeline) {
      return "6px 6px 0px 0px";
    }

    if (isFirstVariantMedia) {
      return "0px 0px 0px 6px";
    }

    if (hasVariantMedia) {
      return "6px 0px 0px 0px";
    }

    return "6px 0px 0px 6px";
  }

  if (isFirstVariantMedia) {
    return "0px 6px 6px 6px";
  }

  if (hasVariantMedia) {
    return "6px 6px 6px 0px";
  }

  return "6px";
};

export const getTimelineItemBowShadow = ({
  isDragging,
  isVerticalTimeline,
  truncated,
}: {
  isDragging: boolean;
  isVerticalTimeline: boolean;
  truncated: Truncated;
}): string => {
  if (
    isVerticalTimeline &&
    (truncated === "end" || truncated === "start-end")
  ) {
    return "unset";
  }

  if (isDragging) {
    return "0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2)";
  }

  return "0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)";
};

const getContrastTextColor = (backgroundColor: string): string =>
  chroma.contrast(backgroundColor, "white") > 2 ? "white" : "black";

export const getItemRendererStyle = ({
  color,
  truncated,
  isVerticalTimeline,
  isDragging = false,
  isSelected = false,
  showDropZone = false,
  canDrop = false,
  isOver = false,
  canSelect = false,
  isFirstVariantMedia = false,
  hasVariantMedia = false,
  isLoading = false,
}: {
  color: string;
  isDragging?: boolean;
  isSelected?: boolean;
  showDropZone?: boolean;
  canDrop?: boolean;
  isOver?: boolean;
  canSelect?: boolean;
  isVerticalTimeline: boolean;
  isFiller?: boolean;
  isFirstVariantMedia?: boolean | null;
  hasVariantMedia?: boolean | null;
  truncated: Truncated;
  isLoading?: boolean;
}): CSSProperties => {
  const background = getTimelineItemBackground({
    color,
    isDragging,
    isSelected,
    showDropZone,
    canDrop,
    isOver,
  });

  const border = getTimelineItemBorder({
    color,
    isDragging,
    showDropZone,
    canDrop,
    truncated,
    isVertical: isVerticalTimeline,
  });

  const bowShadow = getTimelineItemBowShadow({
    isDragging,
    isVerticalTimeline,
    truncated,
  });

  const getTimelineItemCursor = (): string => {
    if (isLoading) return "not-allowed";
    if (canSelect) return "pointer";
    return "default";
  };

  return {
    ...background,
    ...border,
    height: "100%",
    color: showDropZone
      ? "var(--foreground)"
      : getContrastTextColor(background.backgroundColor),
    fontWeight: isSelected ? "bold" : "normal",
    borderRadius: getBorderRadiusTimelineItem({
      truncated,
      isVerticalTimeline,
      isFirstVariantMedia,
      hasVariantMedia,
    }),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    transitionProperty: "background, box-shadow",
    boxShadow: bowShadow,
    cursor: getTimelineItemCursor(),
  };
};

export const getPreviewItemRendererStyle = ({
  size,
  startPosition,
  isVertical,
  isOnBaseline,
  color = DEFAULT_MEDIA_COLOR,
  truncated,
  truncatedDropzone,
}: {
  color?: string;
  size: number;
  startPosition: number;
  isVertical: boolean;
  truncated: Truncated;
  isOnBaseline?: boolean;
  truncatedDropzone: Truncated;
}): CSSProperties => {
  const backgroundColor = chroma(color).brighten(0.75).hex();
  const itemDimensions = getPreviewMediaItemDimensions(
    size,
    startPosition,
    isVertical,
    truncatedDropzone,
    isOnBaseline,
  );

  const border = getTimelineItemBorder({
    color,
    truncated,
    isVertical,
  });

  const bowShadow = getTimelineItemBowShadow({
    isDragging: true,
    isVerticalTimeline: isVertical,
    truncated,
  });

  return {
    ...itemDimensions,
    ...border,
    backgroundColor,
    borderRadius: getBorderRadiusTimelineItem({
      truncated,
      isVerticalTimeline: isVertical,
    }),
    color: getContrastTextColor(backgroundColor),
    boxShadow: bowShadow,
  };
};

const areMediaNextToEachOther = (media: Media, nextMedia: Media): boolean =>
  moment(media.endTime).valueOf() + 1 === moment(nextMedia.startTime).valueOf();

export const getSchedulingTimeslotItems = (
  items: Media[],
): SchedulingTimeslotItem[] => {
  const mediaListOnBaseline = items
    .filter((item) => item.isOnBaseline)
    .sort(
      (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf(),
    );

  return mediaListOnBaseline.reduce(
    (acc: AccSchedulingTimeslotItems, currentItem, currentIndex) => {
      if (currentItem.timeslot?.uuid && currentItem.timeslot?.name) {
        const prevMedia = mediaListOnBaseline[currentIndex - 1];
        const prevMediaIsFromSameTemplate =
          prevMedia?.timeslot?.uuid === currentItem.timeslot?.uuid;
        const prevMediaIsFromSameTemplateAndNextToCurrent =
          prevMediaIsFromSameTemplate &&
          areMediaNextToEachOther(prevMedia, currentItem);

        const hasMediaVariant = currentItem.variantMediaList
          ? currentItem.variantMediaList.length > 0
          : false;

        const isFirstMedia =
          currentIndex === 0 || !prevMediaIsFromSameTemplateAndNextToCurrent;
        const nextMedia = mediaListOnBaseline[currentIndex + 1];
        const isNextMediaFromSameTemplate =
          nextMedia && nextMedia.timeslot?.uuid === currentItem.timeslot?.uuid;
        const hasNextMedia =
          isNextMediaFromSameTemplate &&
          areMediaNextToEachOther(currentItem, nextMedia);
        const startTime = moment(currentItem.startTime);
        const endTime = moment(currentItem.endTime);

        const itemIdList =
          currentItem?.itemIdList ?? new Set([currentItem.uuid]);

        if (isFirstMedia) {
          acc.currentTemplate = {
            type: OBJECT_TYPES_SCHEDULE_TEMPLATE,
            id: uuidv4(),
            group: currentItem.scheduleId,
            title: currentItem.timeslot.name,
            start_time: startTime,
            end_time: endTime,
            timeslot: currentItem.timeslot,
            scheduleId: currentItem.scheduleId,
            updatedAt: currentItem.updatedAt,
            userId: currentItem.userId,
            hasMediaVariant,
            itemIdList,
          };
        }

        if (acc.currentTemplate && hasNextMedia && !isFirstMedia) {
          acc.currentTemplate.hasMediaVariant =
            hasMediaVariant || acc.currentTemplate.hasMediaVariant;
          acc.currentTemplate.itemIdList = new Set([
            ...acc.currentTemplate.itemIdList,
            ...itemIdList,
          ]);
          acc.currentTemplate.end_time = endTime;
        }

        if (acc.currentTemplate && !hasNextMedia) {
          acc.currentTemplate.end_time = endTime;
          acc.currentTemplate.hasMediaVariant =
            hasMediaVariant || acc.currentTemplate.hasMediaVariant;
          acc.currentTemplate.itemIdList = new Set([
            ...acc.currentTemplate.itemIdList,
            ...itemIdList,
          ]);
          acc.timeslotList.push(acc.currentTemplate);
          delete acc.currentTemplate;
        }
      }

      return acc;
    },
    { timeslotList: [] },
  ).timeslotList;
};

export const parseCssTextValue = (value: string): number => {
  const parsedValue = value.split("px")[0];
  return Number(parsedValue);
};

export function getTimelineItemPaddings({
  isVerticalTimeline,
  width,
}: {
  isVerticalTimeline: boolean;
  width?: string | number;
}): Record<string, string | number> {
  const defaultPadding = {
    paddingBottom: isVerticalTimeline ? "1px" : 0,
    paddingTop: isVerticalTimeline ? "1px" : 0,
    paddingLeft: isVerticalTimeline ? 0 : "1px",
    paddingRight: isVerticalTimeline ? 0 : "1px",
  };

  const noPadding = {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
  };

  if (!width) return defaultPadding;

  if (typeof width === "string") {
    return parseCssTextValue(width) < 3 ? noPadding : defaultPadding;
  }

  return width < 3 ? noPadding : defaultPadding;
}

export const getTimelineMiddle = ({
  startTime,
  endTime,
}: {
  startTime: number;
  endTime: number;
}): Moment => moment(startTime).add((endTime - startTime) / 2, "milliseconds");

export const getTimeboundsQueryString = (date: Date): string => {
  const startTime = moment(date).valueOf();
  const endTime = moment(date).clone().add(12, "hours").valueOf();

  return `?${TimeboundKeys.StartTime}=${startTime}&${TimeboundKeys.EndTime}=${endTime}`;
};

export const getTimelinePeriod = ({
  startTime,
  endTime,
  unit = "minutes",
}: {
  startTime: number;
  endTime: number;
  unit?: unitOfTime.Diff;
}): number => moment(endTime).diff(startTime, unit);

export const getComputedTimelineItemsKeyName = (
  isClustered: boolean,
): ComputedTimelineItemsType =>
  isClustered
    ? ComputedTimelineItemsType.WITH_CLUSTER
    : ComputedTimelineItemsType.WITHOUT_CLUSTER;

export const getTimelineBufferStartDate = (): number =>
  moment().add(5, "minute").valueOf();

export const convertFirstDayOfWeekStringToNumber = (
  firstDayOfWeek?: WeekDays,
): number => {
  switch (firstDayOfWeek) {
    case WeekDays.SUNDAY:
      return 0;
    case WeekDays.MONDAY:
      return 1;
    case WeekDays.TUESDAY:
      return 2;
    case WeekDays.WEDNESDAY:
      return 3;
    case WeekDays.THURSDAY:
      return 4;
    case WeekDays.FRIDAY:
      return 5;
    case WeekDays.SATURDAY:
      return 6;
    default:
      return 1;
  }
};
